// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import type { ThemeMode } from "../../context/AppThemeContext";
import { ApiServiceError } from "../../services/api/apiError";
import { apiService } from "../../services/api/apiService";
import { APPEARANCE_PREFERENCE_KEY, parseAppearanceValue } from "./appearanceSync";
import { shouldUseDemoData } from "../../services/api/demoData";

export interface AppearanceApiContext {
  token?: string;
  allowTestModeRequests?: boolean;
}

interface AppearancePreferenceDto {
  key?: string;
  value?: string;
}

interface ApiEnvelope<T> {
  data?: T;
  error?: { message?: string };
}

function tokenOption(
  context: AppearanceApiContext,
): { token: string } | Record<string, never> {
  return context.token !== undefined ? { token: context.token } : {};
}

function unwrap<T>(envelope: ApiEnvelope<T>): T {
  if (envelope.error?.message !== undefined) {
    throw new Error(envelope.error.message);
  }

  if (envelope.data === undefined) {
    throw new Error("API response was missing the expected data payload.");
  }

  return envelope.data;
}

export async function readAppearancePreference(
  context: AppearanceApiContext,
): Promise<ThemeMode | null> {
  if (shouldUseDemoData(context)) {
    return null;
  }

  try {
    const response = await apiService.requestJson<ApiEnvelope<AppearancePreferenceDto>>({
      path: `/api/me/preferences/${APPEARANCE_PREFERENCE_KEY}`,
      ...tokenOption(context),
    });
    return parseAppearanceValue(unwrap(response.unwrap()).value);
  } catch (error: unknown) {
    if (error instanceof ApiServiceError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function writeAppearancePreference(
  context: AppearanceApiContext,
  mode: ThemeMode,
): Promise<void> {
  if (shouldUseDemoData(context)) {
    return;
  }

  const response = await apiService.requestJson<
    ApiEnvelope<AppearancePreferenceDto>,
    { key: string; value: string }
  >({
    path: "/api/me/preferences",
    method: "PUT",
    ...tokenOption(context),
    body: { key: APPEARANCE_PREFERENCE_KEY, value: mode },
  });
  unwrap(response.unwrap());
}
